import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import type { NetworkAuditLogs__User as User } from '~/components/AuditLogs/queries/list.generated';

export interface FilterItem {
  label: string;
  value: string;
  selected: boolean;
  count?: number;
}

export interface FilterState {
  expanded: boolean;
  items: FilterItem[];
  panelOpen: boolean;
}

export interface FiltersState {
  recordTypes: FilterState;
  userIds: FilterState;
}

const getInitialRecordTypes = (t: (key: string) => string): FilterItem[] => [
  { label: t('common.record_types.channel'), selected: false, value: 'Channel' },
  { label: t('common.record_types.channel_guide'), selected: false, value: 'ChannelGuide' },
  { label: t('common.record_types.content_folder'), selected: false, value: 'ContentFolder' },
  { label: t('common.record_types.content_item'), selected: false, value: 'ContentItem' },
  { label: t('common.record_types.data_source'), selected: false, value: 'DataSource' },
  { label: t('common.record_types.design'), selected: false, value: 'Design' },
  { label: t('common.record_types.design_folder'), selected: false, value: 'DesignFolder' },
  { label: t('common.record_types.device'), selected: false, value: 'Device' },
  { label: t('common.record_types.device_group'), selected: false, value: 'DeviceGroup' },
  { label: t('common.record_types.event'), selected: false, value: 'Event' },
  { label: t('common.record_types.event_type'), selected: false, value: 'EventType' },
  { label: t('common.record_types.font'), selected: false, value: 'Font' },
  { label: t('common.record_types.layout'), selected: false, value: 'Layout' },
  { label: t('common.record_types.media_item'), selected: false, value: 'MediaItem' },
  { label: t('common.record_types.network'), selected: false, value: 'Network' },
  { label: t('common.record_types.overlay'), selected: false, value: 'Overlay' },
  { label: t('common.record_types.playlist'), selected: false, value: 'Playlist' },
  { label: t('common.record_types.playlist_folder'), selected: false, value: 'PlaylistFolder' },
  { label: t('common.record_types.property_group'), selected: false, value: 'PropertyGroup' },
  { label: t('common.record_types.role'), selected: false, value: 'Role' },
  { label: t('common.record_types.show'), selected: false, value: 'Show' },
  { label: t('common.record_types.show_folder'), selected: false, value: 'ShowFolder' },
  { label: t('common.record_types.signal'), selected: false, value: 'DeviceSignal' },
  { label: t('common.record_types.tv_brand'), selected: false, value: 'TVBrand' },
  { label: t('common.record_types.user'), selected: false, value: 'User' },
  { label: t('common.record_types.zone'), selected: false, value: 'Zone' },
];

export const useSelectedFilterParams = () => {
  const [searchParams] = useSearchParams();
  return {
    recordTypes: JSON.parse(searchParams.get('record-types') || '[]') as string[],
    userIds: (JSON.parse(searchParams.get('users') || '[]') as string[]).map(Number),
  };
};

export const useFilters = (initialUsers: readonly User[] | undefined) => {
  const { t } = useTranslation();
  const { recordTypes: recordTypesParams, userIds: userIdsParams } = useSelectedFilterParams();

  const initRecordTypeState = useCallback((): FilterState => {
    return {
      expanded: true,
      items: getInitialRecordTypes(t).map((item) => ({
        label: item.label,
        value: item.value,
        selected: recordTypesParams.includes(item.value),
      })),
      panelOpen: recordTypesParams.length > 0,
    };
  }, [recordTypesParams, t]);

  const initUserIdsState = useCallback((): FilterState => {
    return {
      expanded: true,
      items: initialUsers
        ? initialUsers.map((user) => ({
            label: user.name,
            value: user.id.toString(),
            selected: userIdsParams.includes(user.id),
          }))
        : [],
      panelOpen: userIdsParams.length > 0,
    };
  }, [initialUsers, userIdsParams]);

  const [filters, setFilters] = useState<FiltersState>({
    recordTypes: initRecordTypeState(),
    userIds: initUserIdsState(),
  });

  const initialUsersRef = useRef<readonly User[] | undefined>(initialUsers);

  useEffect(() => {
    if (!initialUsers || initialUsers.length === 0) return;
    if (initialUsersRef.current !== initialUsers) {
      setFilters((prev) => ({ ...prev, userIds: initUserIdsState() }));
      initialUsersRef.current = initialUsers;
    }
  }, [initialUsers, initUserIdsState]);

  const [_, setSearchParams] = useSearchParams();

  const updateSearchParams = useCallback(
    (key: keyof FiltersState, items: FilterItem[]) => {
      const urlKeys: Record<keyof FiltersState, string> = {
        recordTypes: 'record-types',
        userIds: 'users',
      };
      setSearchParams((params) => {
        const selected = items.filter((x) => x.selected);
        if (selected.length > 0) {
          params.set(urlKeys[key], JSON.stringify(selected.map((x) => x.value)));
        } else {
          params.delete(urlKeys[key]);
        }
        params.set('page', '1'); // reset pagination when filters change
        return params;
      });
    },
    [setSearchParams],
  );

  const clearFilters = useCallback(() => {
    setFilters((current) => ({
      recordTypes: {
        ...current.recordTypes,
        expanded: true,
        items: current.recordTypes.items.map((item) => ({ ...item, selected: false })),
        panelOpen: false,
      },
      userIds: {
        ...current.userIds,
        expanded: true,
        items: current.userIds.items.map((item) => ({ ...item, selected: false })),
        panelOpen: false,
      },
    }));
    setSearchParams((params) => {
      params.delete('record-types');
      params.delete('users');
      params.delete('search');
      params.set('page', '1');
      return params;
    });
  }, [setSearchParams]);

  const setFilterItems = useCallback(
    (key: keyof FiltersState, newItems: FilterItem[]) => {
      setFilters((x) => ({ ...x, [key]: { ...x[key], items: newItems } }));
      updateSearchParams(key, newItems);
    },
    [updateSearchParams],
  );

  const toggleAccordion = useCallback((key: keyof FiltersState) => {
    setFilters((x) => ({ ...x, [key]: { ...x[key], expanded: !x[key].expanded } }));
  }, []);

  const toggleItem = useCallback(
    (key: keyof FiltersState, value: string) => {
      const newItems = filters[key].items.map((item) =>
        item.value === value ? { ...item, selected: !item.selected } : item,
      );
      setFilters((prev) => ({ ...prev, [key]: { ...prev[key], items: newItems } }));
      updateSearchParams(key, newItems);
    },
    [filters, updateSearchParams],
  );

  const togglePanel = useCallback((key: keyof FiltersState) => {
    setFilters((current) => ({
      ...current,
      [key]: { ...current[key], panelOpen: !current[key].panelOpen },
    }));
  }, []);

  return {
    filters,
    clearFilters,
    setFilterItems,
    toggleAccordion,
    toggleItem,
    togglePanel,
  };
};
